import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ACCESS_TOKEN, API_BASE_URL } from '../../assets/constants'
import {
	attempt401RefreshRetry,
	scheduleProactiveAccessTokenRefresh
} from '../auth/tokenRefresh'
import ROUTES from '../../assets/routes'
import commonActions from '../../actions/common.actions'
import store from '../../store'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { isAuthenticated } from '../../util/authentication'
import { ErrorResponse, Platform, platformToJSON, RequestHeader, requestHeaderToJSON } from '../../proto/generated/common'
import { BackendProvider } from '../../adapters/types'
import { Tick2TradeAdapter } from '../../adapters/Tick2TradeAdapter'
import { ShoonyaAdapter } from '../../adapters/ShoonyaAdapter'
import type { IBackendAdapter, BackendConfig } from '../../adapters/types'
import { applyClientVersionHeader, handleClientVersionError, handleClientVersionSuccessResponse} from '../../util/clientVersion'

const BUILD_TIME_PROVIDER = (import.meta.env.VITE_BACKEND_PROVIDER || 'tick2trade') as BackendProvider

function getBuildTimeConfig(provider: BackendProvider): BackendConfig {
	const API_BASE_SERVER = import.meta.env.VITE_API_BASE_SERVER
	const USE_SSL = import.meta.env.VITE_USE_SSL === 'true'
	const API_SCHEME = USE_SSL ? 'https://' : 'http://'
	const WS_SCHEME = USE_SSL ? 'wss://' : 'ws://'

	switch (provider) {
		case BackendProvider.TICK2TRADE:
			const tick2tradeConfig = {
				provider: BackendProvider.TICK2TRADE,
				baseUrl: API_SCHEME + API_BASE_SERVER,
				wsUrl: WS_SCHEME + API_BASE_SERVER + '/app'
			}
			return tick2tradeConfig

		case BackendProvider.SHOONYA:
			const shoonyaUrl = import.meta.env.VITE_shoonya_API_URL
			const shoonyaWsUrl = import.meta.env.VITE_shoonya_WS_URL
			const shoonyaApiKey = import.meta.env.VITE_shoonya_API_KEY

			if (!shoonyaUrl) {
				return {
					provider: BackendProvider.TICK2TRADE,
					baseUrl: API_SCHEME + API_BASE_SERVER,
					wsUrl: WS_SCHEME + API_BASE_SERVER + '/app'
				}
			}

			const shoonyaConfig = {
				provider: BackendProvider.SHOONYA,
				baseUrl: shoonyaUrl,
				wsUrl: shoonyaWsUrl || shoonyaUrl.replace('http', 'ws'),
				apiKey: shoonyaApiKey,
				headers: {
					'X-Client-ID': import.meta.env.VITE_shoonya_CLIENT_ID || ''
				}
			}
			return shoonyaConfig

		default:
			console.warn(`[Adapters] Unknown provider: ${provider}, defaulting to tick2trade`)
			return {
				provider: BackendProvider.TICK2TRADE,
				baseUrl: API_SCHEME + API_BASE_SERVER,
				wsUrl: WS_SCHEME + API_BASE_SERVER + '/app'
			}
	}
}

function createAdapter(): IBackendAdapter {
	const config = getBuildTimeConfig(BUILD_TIME_PROVIDER)

	let adapter: IBackendAdapter

	switch (BUILD_TIME_PROVIDER) {
		case BackendProvider.TICK2TRADE:
			adapter = new Tick2TradeAdapter(config)
			break

		case BackendProvider.SHOONYA:
			adapter = new ShoonyaAdapter(config)
			break

		default:
			adapter = new Tick2TradeAdapter(config)
	}

	return adapter
}

const adapter = createAdapter()

export const currentAdapter = adapter

const isUnloadError = (error: any): boolean => {
	return (
		error.code === 'ERR_CANCELED' ||
		error.message === 'canceled' ||
		error.name === 'AbortError' ||
		error.code === 'ERR_NETWORK' ||
		!error.response
	);
};

const api = axios.create({
	baseURL: adapter.getBaseUrl()
})

const requestPathsToBeControlled: string[] = ['/options', '/futures', '/security-snapshots']

interface QueuedRequest {
	resolve: (value?: AxiosResponse<any> | PromiseLike<AxiosResponse<any>>) => void;
	reject: (reason?: any) => void;
}

const inProgressRequests = new Map<string, QueuedRequest[]>();

const reqInProgressMessage = 'Request already in progress'

export const rawApi = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		'Content-Type': 'application/json'
	}
})

rawApi.interceptors.request.use((config: any) => {
	const token = localStorage.getItem(ACCESS_TOKEN)
	if (token) {
		config.headers = config.headers || {}
		config.headers['Authorization'] = `Bearer ${token}`
	}
    applyClientVersionHeader(config)
	return config
})

rawApi.interceptors.response.use(
	(r) => r,
	async (error) => {
		const recovered = await attempt401RefreshRetry(rawApi, error)
		if (recovered) {
			return recovered
		}
		return Promise.reject(error)
	}
)

api.interceptors.request.use(
	(config: any) => {
		const token = localStorage.getItem(ACCESS_TOKEN)
		const requestId = (config?.url ?? '') + config.method + JSON.stringify(config?.params ?? {}) + JSON.stringify(config?.data ?? {})
		if (token) {
			if (!isAuthenticated()) {
				currentAdapter.logout()
				window.open(window.origin + ROUTES.login, '_self')
				return Promise.reject(new Error('Request stopped by interceptor'));
			}
			config.headers['Authorization'] = 'Bearer ' + token
		}
		config.headers['Content-Type'] = 'application/json'
		config.headers[requestHeaderToJSON(RequestHeader.PLATFORM)] = platformToJSON(Platform.WEB)
		config.headers[requestHeaderToJSON(RequestHeader.ENVIRONMENT)] = import.meta.env.MODE;
		applyClientVersionHeader(config);

		(config as any).originalUrl = config.url

		try {
			const transformed = adapter.transformRequest(
				config.url || '',
				config.data,
				config.method || 'GET'
			)

			config.url = transformed.url
			config.data = transformed.data
			if (transformed.headers) {
				config.headers = { ...config.headers, ...transformed.headers }
			}
		} catch (error) {
			console.error('[Adapter] Request transformation error:', error)
		}

		if (requestPathsToBeControlled.some((path) => (config?.url ?? '').includes(path))) {
			if (inProgressRequests.has(requestId)) {
				throw {
					isLocal: true, promise: new Promise<AxiosRequestConfig>((resolve, reject) => {
						inProgressRequests.get(requestId)?.push({ resolve: resolve as any, reject });
					})
				};
			} else {
				(config as any).requestId = requestId;
				inProgressRequests.set(requestId, []);
				return config;
			}
		}
		return config
	},
	error => {
		return error?.isLocal ? error.promise : Promise.reject(error)
	}
)

api.interceptors.response.use(
	response => {
		const requestId = (response.config as any).requestId;
		const queuedRequests = inProgressRequests.get(requestId);

		if (queuedRequests) {
			queuedRequests.forEach(({ resolve }) => resolve(response));
			inProgressRequests.delete(requestId);
		}

		handleClientVersionSuccessResponse(response)

		const originalUrl = (response.config as any).originalUrl || response.config.url || ''

		try {
			response.data = adapter.transformResponse(response.data, originalUrl)
		} catch (error) {
			console.error('[Adapter] Response transformation error:', error)
		}

		return response
	},
	async (error) => {
		const requestId = (error?.config as any)?.requestId;
		const queuedRequests = inProgressRequests.get(requestId);
		const suppressError = error?.config?.headers?.['X-Suppress-Error'] === 'true';

		if (isUnloadError(error)) {
			if (queuedRequests) {
				queuedRequests.forEach(({ reject }) => reject(error));
				inProgressRequests.delete(requestId);
			}
			return Promise.reject(error);
		}

		if (!error.response) {
			if (queuedRequests) {
				queuedRequests.forEach(({ reject }) => reject(error));
				inProgressRequests.delete(requestId);
			}
			if (!suppressError && !isUnloadError(error)) {
				commonActions.setApiError(store.dispatch, {
					error: true,
					message: 'Network error occurred',
					errorType: 'error'
				})
			}
			return Promise.reject(error);
		}

		if (handleClientVersionError(error)) {
			if (queuedRequests) {
				queuedRequests.forEach(({ reject }) => reject(error));
				inProgressRequests.delete(requestId);
			}
			return Promise.reject(error);
		}

		if (error?.isLocal) {
			return await error.promise
		}

		const recovered = await attempt401RefreshRetry(api, error)
		if (recovered) {
			return recovered
		}

		if (queuedRequests) {
			queuedRequests.forEach(({ reject }) => reject(error));
			inProgressRequests.delete(requestId);
		}

		const transformedError = adapter.transformError(error)

		let parsedError: any
		try {
			parsedError = JSON.parse(error?.message)
		} catch (e) {
			const message = ErrorResponse.decode(new Uint8Array(error?.response?.data))?.message
			if (!suppressError) {
				if (error.response?.status === 401) {
					currentAdapter.logout()
					AnalyticsManager.reset()
					if (window.location.pathname !== ROUTES.login) {
						window.open(window.origin + ROUTES.login, '_self')
					}
					return
				}
				commonActions.setApiError(store.dispatch, {
					error: true,
					message: message ? message : 'Something went wrong',
					errorType: 'error'
				})
				if (error.response?.status === 432) {
					window.open(window.origin + ROUTES.blocked, '_self')
					return
				} else if (error.response?.status === 433) {
					window.open(window.origin + ROUTES.waitlist, '_self')
					return
				}
				return Promise.reject(transformedError)
			}
		}

		if (!suppressError) {
			if (parsedError?.message !== reqInProgressMessage) {
				if (error.response.status === 401) {
					currentAdapter.logout()
					if (window.location.pathname !== ROUTES.login) {
						window.open(window.origin + ROUTES.login, '_self')
					}
					return
				}
				const message = ErrorResponse.decode(new Uint8Array(error.response.data))?.message
				commonActions.setApiError(store.dispatch, {
					error: true,
					message: error?.response?.data ? message : `Something went wrong`,
					errorType: 'error'
				})
				if (error.response.status === 432) {
					window.open(window.origin + ROUTES.blocked, '_self')
					return
				} else if (error.response.status === 433) {
					window.open(window.origin + ROUTES.waitlist, '_self')
					return
				}
			}
		}
		return Promise.reject(transformedError)
	}
)

scheduleProactiveAccessTokenRefresh()

export default api
